import React, { useState } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { Formik } from "formik";
import { Row, Col, Select, Checkbox, Divider } from "antd";
import { DebounceInput } from "react-debounce-input";
import { cloneDeep } from "lodash";
import moment from "moment-timezone";

import AdvancedFilterPopover from "../../../../../Common/AdvancedFiltersPopover";
import ShowSelectedAdvanceFilter from "../../../../../Common/ShowSelectedAdvanceFilters";
import CommonRangePicker from "../../../../../../components/date-picker/CommonDatePicker";
import { AGING_OVERDUE_PERIOD_OPTIONS, DELAY } from "../../../../../Common/constants";
import { dateStringApplyOffSet, getSettingKey, preventEnterActions } from "../../../../../Common/utils";

const { Option } = Select;
const AGING_INVOICE_FILTER_KEY = "aging_invoice_filter";

const agingFilterInitialValues = {
  search_key: "",
  start_order_date: null,
  end_order_date: null,
  start_due_date: null,
  end_due_date: null,
  overdue_period: [],
};

export function AgingTableFilters({ setQueryParams }) {
  const [popoverVisible, setPopoverVisible] = useState(false);
  const { basicSettings } = useSelector((state) => ({ basicSettings: state.basicSettings }), shallowEqual);
  const timeZone = getSettingKey(basicSettings.entity, "Basic", "timezone");
  const savedFilters = JSON.parse(localStorage.getItem(AGING_INVOICE_FILTER_KEY));

  const applyAdvanceFilters = (values) => {
    setQueryParams((prev) => ({
      ...prev,
      start_order_date: values.start_order_date ? dateStringApplyOffSet(values.start_order_date, timeZone) : null,
      end_order_date: values.end_order_date ? dateStringApplyOffSet(values.end_order_date, timeZone) : null,
      start_due_date: values.start_due_date ? dateStringApplyOffSet(values.start_due_date, timeZone) : null,
      end_due_date: values.end_due_date ? dateStringApplyOffSet(values.end_due_date, timeZone) : null,
      overdue_period: values.overdue_period || [],
      page: 1,
    }));
  };

  return (
    <Formik initialValues={cloneDeep(savedFilters?.advance_filters || agingFilterInitialValues)} onSubmit={() => {}}>
      {({ values, setFieldValue, resetForm }) => (
        <>
        <Row className="align-items-end mb-3">
          <Col xs={24} sm={24} md={16} lg={10}>
            <label className="search-label font-weight-bold text-xs font-semibold leading-4 tracking-normal text-left text-gray-900">
              Search by Invoice No., Business Name
            </label>
            <DebounceInput
              className="form-control"
              debounceTimeout={DELAY}
              placeholder="Search"
              onKeyDown={preventEnterActions}
              onChange={(e) => {
                const searchKey = e.target.value.trimLeft();
                setFieldValue("search_key", searchKey);
                setQueryParams((prev) => ({ ...prev, search_key: searchKey, page: 1 }));
              }}
            />
          </Col>
          <Col xs={24} sm={24} md={4} lg={3} className="mt-3 mt-md-0 ml-2">
            <AdvancedFilterPopover
              placement="bottom"
              zIndex={0}
              title="Advance Filters"
              overlayClassName="stocks-advance-filter-popover-title"
              visible={popoverVisible}
              onVisibleChange={setPopoverVisible}
              contentWidth={376}
              content={
                <div>
                  <Row>
                    <Col xs={24} sm={24} md={24} lg={24} className="mb-3">
                      <label className="font-montserrat text-xs font-medium leading-4 tracking-normal text-left mb-1 d-block">
                        Select Invoice Start and End Date
                      </label>
                      <CommonRangePicker
                        type="range-picker"
                        allowClear
                        value={[values.start_order_date ? moment(values.start_order_date) : "", values.end_order_date ? moment(values.end_order_date) : ""]}
                        onChange={(value, dateStrings) => {
                          setFieldValue("start_order_date", dateStrings?.[0] || null);
                          setFieldValue("end_order_date", dateStrings?.[1] || null);
                        }}
                      />
                    </Col>
                    <Col xs={24} sm={24} md={24} lg={24} className="mb-3">
                      <label className="font-montserrat text-xs font-medium leading-4 tracking-normal text-left mb-1 d-block">
                        Select Due Start and End Date
                      </label>
                      <CommonRangePicker
                        type="range-picker"
                        allowClear
                        value={[values.start_due_date ? moment(values.start_due_date) : "", values.end_due_date ? moment(values.end_due_date) : ""]}
                        onChange={(value, dateStrings) => {
                          setFieldValue("start_due_date", dateStrings?.[0] || null);
                          setFieldValue("end_due_date", dateStrings?.[1] || null);
                        }}
                      />
                    </Col>
                    <Col xs={24} sm={24} md={24} lg={24} className="mb-2">
                      <label className="font-montserrat text-xs font-medium leading-4 tracking-normal text-left mb-1 d-block">
                        Filter by Overdue Period
                      </label>
                      <Select
                        className="advance-filter"
                        style={{ width: "100%" }}
                        mode="multiple"
                        showArrow
                        allowClear
                        placeholder="Select Overdue Period"
                        value={values.overdue_period || []}
                        onChange={(selected) => {
                          setFieldValue("overdue_period", selected);
                        }}
                        dropdownRender={(menu) => {
                          const allValues = AGING_OVERDUE_PERIOD_OPTIONS.map((option) => option.value);
                          const selectedCount = (values.overdue_period || []).length;
                          const isAllSelected = selectedCount === allValues.length;
                          const isPartiallySelected = selectedCount > 0 && !isAllSelected;

                          return (
                            <div>
                              <div
                                style={{ padding: "5px 12px", cursor: "pointer" }}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => setFieldValue("overdue_period", isAllSelected ? [] : allValues)}
                              >
                                <Checkbox checked={isAllSelected} indeterminate={isPartiallySelected}>
                                  Select All
                                </Checkbox>
                              </div>
                              <Divider style={{ margin: "4px 0" }} />
                              {menu}
                            </div>
                          );
                        }}
                      >
                        {AGING_OVERDUE_PERIOD_OPTIONS.map((option) => (
                          <Option key={option.value} value={option.value}>
                            {option.label}
                          </Option>
                        ))}
                      </Select>
                    </Col>
                  </Row>
                </div>
              }
              trigger="click"
              onClear={() => {
                resetForm({ values: cloneDeep(agingFilterInitialValues) });
                applyAdvanceFilters(agingFilterInitialValues);
                setQueryParams((prev) => ({ ...prev, search_key: "", page: 1 }));
                localStorage.setItem(AGING_INVOICE_FILTER_KEY, JSON.stringify({ show_advance_filters: false }));
                setPopoverVisible(false);
              }}
              onApply={() => {
                applyAdvanceFilters(values);
                localStorage.setItem(
                  AGING_INVOICE_FILTER_KEY,
                  JSON.stringify({ advance_filters: values, show_advance_filters: true })
                );
                setPopoverVisible(false);
              }}
            />
          </Col>
        </Row>
        <ShowSelectedAdvanceFilter popoverVisible={popoverVisible} moduleSearchFilter="aging_invoice" module="dashboard" />
        </>
      )}
    </Formik>
  );
}
